import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { NcDto } from "@audit-plan/shared";
import { useNcs } from "../../api/ncs";
import { MonitoringOffPill, Pill, SeverityPill, fmtD } from "../pills";
import { ResponsiveList, monoStyle } from "../ResponsiveList";

/** NC dello stabilimento (AUD-F8.4: la NC sta sullo stabilimento, non sul fornitore). Il clic apre la NC nella sua pagina. */
export function FactoryNcs({ factoryId, onLeave }: { factoryId: string; onLeave?: () => void }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const q = useNcs({ factoryId });
  const rows = [...(q.data ?? [])].sort((a, b) => Number(a.status === "closed") - Number(b.status === "closed") || (b.createdAt > a.createdAt ? 1 : -1));
  const open = (n: NcDto) => {
    onLeave?.();
    nav(`/ncs?id=${n.id}`);
  };
  const statusPill = (n: NcDto) => (
    <>
      <Pill tone={n.status === "closed" ? "ok" : "bad"}>{n.status === "closed" ? t("closed") : t("open")}</Pill>
      {n.status === "open" && <MonitoringOffPill off={n.monitoringOff} />}
    </>
  );
  return (
    <ResponsiveList<NcDto>
      rowKey="id"
      size="small"
      loading={q.isLoading}
      dataSource={rows}
      pageSize={10}
      pageSizeKey="factoryNcs"
      emptyText={t("none")}
      onRowClick={open}
      card={{
        code: (n) => `${n.code}${n.auditCode ? ` · ${n.auditCode}` : ""}`,
        title: (n) => n.title,
        tags: (n) => (
          <>
            <SeverityPill s={n.severity} />
            {statusPill(n)}
          </>
        ),
        fields: (n) => [[t("openDate"), fmtD(n.createdAt)]],
      }}
      columns={[
        { title: t("code"), dataIndex: "code", render: (c: string) => <span style={monoStyle}>{c}</span> },
        { title: t("severity"), render: (_: unknown, n) => <SeverityPill s={n.severity} /> },
        { title: t("title"), dataIndex: "title" },
        { title: "Audit", dataIndex: "auditCode", render: (c: string | null) => (c ? <span style={{ ...monoStyle, fontSize: 12 }}>{c}</span> : <span style={{ fontSize: 12, opacity: 0.6 }}>{t("reg_badge")}</span>) },
        { title: t("openDate"), dataIndex: "createdAt", render: (d: string) => fmtD(d) },
        { title: t("status"), render: (_: unknown, n) => <span style={{ whiteSpace: "nowrap" }}>{statusPill(n)}</span> },
      ]}
    />
  );
}
